/** COSTUL ELEMENTULUI ALES pe machetă (Cătălin, 15.10.2026: «cât m-ar costa
 *  orice element pe care îl selectez, fie el geam, fațadă pe nivel, placări»)
 *  — partea PURĂ: CE intră în cost. Prețul îl pune serverul
 *  (`/fatada/cost-elemente`), cu aceeași clasificare și aceleași tarife ca
 *  devizul fațadei, deci elementul și devizul nu se pot contrazice.
 *
 *    panou de fațadă   el și STRATURILE din spatele/din fața lui pe aceeași
 *                      față, etaj și suprafață (polistiren, tencuială de bază,
 *                      placaj), fiecare cu partea care se suprapune cu el —
 *                      fațada pe nivel costă cu tot ce o face
 *    placa ventilatei  stiva panoului ei, în proporția plăcii
 *    fereastră, ușă    tâmplăria, plus glaful și șpaleții golului (panourile
 *                      care îl au în `hosts`)
 *    balustradă        ea, cu mâna curentă
 *    mâna curentă      numai ea
 *
 *  Nimic nu se stochează: se cere la selecție. */
import type { CanonicalDoc } from "../canonical";
import { fisaElement, indexul, props } from "./selectie";

type Obj = CanonicalDoc["objects"][number];

export interface BucataCost {
  obiect: Obj;
  /** Partea obiectului care intră (1 = tot). */
  factor?: number;
  doar?: "mana" | "profil";
}

export interface CerereCost {
  bucati: BucataCost[];
  sistem?: unknown;
}

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

/** Obiectul de trimis: fără lista plăcilor (poate avea mii de rânduri și nu
 *  schimbă prețul — placajul se plătește pe m²). */
function usor(o: Obj): Obj {
  const pan = props(o, "architecture.panel");
  const jt = pan.joints as Record<string, unknown> | undefined;
  if (!jt || !Array.isArray(jt.tiles)) return o;
  const { tiles: _t, ...rest } = jt;
  return { ...o, props: { ...(o.props ?? {}), "architecture.panel": { ...pan, joints: rest } } } as Obj;
}

/** Intervalul panoului pe față (`u`) și pe înălțime (`z`). */
function cadru(o: Obj): { u: [number, number]; z: [number, number] } | null {
  const pan = props(o, "architecture.panel");
  const g = (o.geometry ?? {}) as Record<string, unknown>;
  const u0 = num(pan.u0), u1 = num(pan.u1);
  if (u0 === undefined || u1 === undefined || u1 <= u0) return null;
  const z0 = num(g.sill_m) ?? 0, h = num(g.height_m) ?? 0;
  return { u: [u0, u1], z: [z0, z0 + h] };
}

const suprapunere = (a: [number, number], b: [number, number]) =>
  Math.max(0, Math.min(a[1], b[1]) - Math.max(a[0], b[0]));

/** Panoul și straturile lui: aceeași față, etaj, suprafață și latură, alt
 *  strat, cu partea suprapusă (pe lungime × pe înălțime). Relieful (brâie,
 *  glafuri) nu e strat: intră numai când e el cel ales. */
function stiva(doc: CanonicalDoc, o: Obj): BucataCost[] {
  const pr = o.properties as Record<string, unknown>;
  const c = cadru(o);
  const out: BucataCost[] = [{ obiect: usor(o) }];
  if (!c) return out;
  for (const x of doc.objects ?? []) {
    if (x.type !== "FacadePanel" || x.id === o.id) continue;
    const px = x.properties as Record<string, unknown>;
    if (px.layer === pr.layer || px.layer === "relief" || px.face !== pr.face || px.storey !== pr.storey
        || px.surface !== pr.surface || px.side !== pr.side || px.building !== pr.building) continue;
    const cx = cadru(x);
    if (!cx) continue;
    const fu = suprapunere(c.u, cx.u) / (cx.u[1] - cx.u[0]);
    const fz = cx.z[1] > cx.z[0] ? suprapunere(c.z, cx.z) / (cx.z[1] - cx.z[0]) : 1;
    if (fu * fz > 0.001) out.push({ obiect: usor(x), factor: fu * fz });
  }
  return out;
}

/** Ce se trimite la preț pentru elementul `eid`; `null` când n-are cost
 *  (un balcon, un obiect necunoscut). */
export function cerereCost(doc: CanonicalDoc | null, eid: string): CerereCost | null {
  if (!doc) return null;
  const fisa = fisaElement(doc, eid);
  const ix = indexul(doc);
  const o = ix.byid.get(fisa?.parte?.din ?? eid);
  if (!fisa || !o) return null;
  const corp = ix.byid.get(String((o.properties as Record<string, unknown>).building ?? ""));
  const sistem = corp ? (props(corp, "architecture.fatada") as { sistem?: unknown }).sistem : undefined;
  const cu = (bucati: BucataCost[]): CerereCost => ({ bucati, ...(sistem ? { sistem } : {}) });

  if (fisa.parte?.fel === "mana") return cu([{ obiect: o, doar: "mana" }]);
  if (fisa.parte?.fel === "profil") return cu([{ obiect: o, doar: "profil" }]);
  if (fisa.parte?.fel === "placa" && fisa.parte.placa) {
    const net = num(props(o, "architecture.panel").net_m2);
    const pl = fisa.parte.placa;
    if (!net) return null;
    const f = Math.min(1, (pl.latime * pl.inaltime) / net);
    return cu(stiva(doc, o).map((b) => ({ ...b, factor: (b.factor ?? 1) * f })));
  }
  if (o.type === "FacadePanel") return cu(stiva(doc, o));
  if (o.type === "Railing") return cu([{ obiect: o }]);
  if (o.type === "Window" || o.type === "Door") {
    const gol = (doc.objects ?? []).filter((x) => x.type === "FacadePanel"
      && ((x.properties as { hosts?: unknown }).hosts as string[] | undefined)?.includes(o.id));
    return cu([{ obiect: o }, ...gol.map((x) => ({ obiect: usor(x) }))]);
  }
  return null;
}
